import React, { useState } from "react";
import "../styles/style.scss";
import { useNavigate } from "react-router-dom";
import { auth, googleProvider, isFirebaseConfigured } from "../firebase-config";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";

function SignIn() {
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const returnUrl = urlSearchParams.get('returnUrl');
  const isOAuth = urlSearchParams.get('oauth') === 'true';
  const oauthReturnTo = urlSearchParams.get('return_to');

  const handleRedirect = () => {
    // Handle OAuth redirect
    if (isOAuth && oauthReturnTo) {
      window.location.href = `${process.env.REACT_APP_API_URL}${decodeURIComponent(oauthReturnTo)}`;
    } else if (
      document.referrer &&
      document.referrer.startsWith(window.location.origin) &&
      returnUrl &&
      !returnUrl.includes("reset-password")
    ) {
      window.location.href = returnUrl;
    } else {
      window.location.href = `${process.env.REACT_APP_CLIENT_URL}/recipes`;
    }
  };

  const handleGoogleSignIn = async () => {
    if (!isFirebaseConfigured() || !auth || !googleProvider) {
      alert("Google sign-in is not configured");
      return;
    }

    setLoading(true);
    try {
      // Sign in with Google popup
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();

      // Send token to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
        credentials: "include",
      });

      const data = await response.json();

      if (data.error) {
        console.error("Error with Google sign-in:", data.error);
        // Show a user-friendly message
        if (data.error.includes("already associated with an email/password account")) {
          alert("This Google account email is already registered. Please sign in using your email and password in the form below.");
        } else {
          alert(data.error);
        }
      } else {
        console.log("Google sign-in successful");
        handleRedirect();
      }
    } catch (error) {
      console.error("Error signing in with Google:", error);
      alert(error.message || "Error signing in with Google");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!emailOrUsername || !password) {
      alert("Email/Username and password are required");
      return;
    }

    setLoading(true);

    try {
      // Check if input is email format
      const isEmail = emailOrUsername.includes('@');

      // If it's an email and Firebase is configured, try Firebase auth first
      if (isEmail && isFirebaseConfigured() && auth) {
        try {
          const userCredential = await signInWithEmailAndPassword(auth, emailOrUsername, password);
          const idToken = await userCredential.user.getIdToken();

          // Verify with backend (backend will set session)
          const response = await fetch(`${process.env.REACT_APP_API_URL}/user/me`, {
            method: "GET",
            headers: {
              'Authorization': `Bearer ${idToken}`,
            },
            credentials: "include",
          });

          if (response.ok) {
            console.log("Firebase sign-in successful");
            handleRedirect();
            return;
          }
        } catch (firebaseError) {
          console.error("Firebase sign-in failed, falling back to legacy auth:", firebaseError);
          console.error("Firebase error code:", firebaseError.code);
          console.error("Firebase error message:", firebaseError.message);
          // Continue to legacy auth below
        }
      }

      // Fallback to legacy backend signin (for username login or non-migrated users)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailOrUsername, password }),
        credentials: "include",
      });

      const data = await response.json();

      if (data.errorMessage) {
        console.error("Error signing in:", data.errorMessage);
        alert(data.errorMessage);
      } else if (data.requiresFirebaseAuth) {
        // User logged in with username, but account uses Firebase
        // Retry authentication with their email
        console.log("Firebase-native user detected, authenticating with Firebase using email:", data.email);

        if (isFirebaseConfigured() && auth) {
          try {
            const userCredential = await signInWithEmailAndPassword(auth, data.email, password);
            const idToken = await userCredential.user.getIdToken();

            // Verify with backend (backend will set session)
            const verifyResponse = await fetch(`${process.env.REACT_APP_API_URL}/user/me`, {
              method: "GET",
              headers: {
                'Authorization': `Bearer ${idToken}`,
              },
              credentials: "include",
            });

            if (verifyResponse.ok) {
              console.log("Firebase sign-in successful (via username lookup)");
              handleRedirect();
              return;
            } else {
              alert("Authentication failed. Please try again.");
            }
          } catch (firebaseError) {
            console.error("Firebase authentication error:", firebaseError);
            alert("Invalid password. Please try again.");
          }
        } else {
          alert("Firebase authentication is not configured");
        }
      } else {
        console.log("User signed in successfully");

        // Show warning if password is too short
        if (data.warning) {
          alert(data.warning);
        }

        handleRedirect();
      }
    } catch (error) {
      console.error("Error signing in:", error);
      alert("An error occurred during sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-4" style={{maxWidth: "450px"}}>
      <div className="shadow-lg rounded p-4" style={{backgroundColor: "white"}}>
        <h2 className="text-center mb-4">Sign In</h2>

        {isOAuth && (
          <div className="alert alert-info mb-3" role="alert" style={{fontSize: "0.9rem"}}>
            Please sign in to connect your Plate Passion account with Alexa.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group mb-3">
            <label htmlFor="emailOrUsername" className="form-label">Email or Username</label>
            <input
              type="text"
              className="form-control"
              id="emailOrUsername"
              name="emailOrUsername"
              value={emailOrUsername}
              onChange={(event) => setEmailOrUsername(event.target.value)}
              placeholder="Enter your email or username"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group mb-3">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              className="form-control"
              id="password"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
          </div>

          <div className="mb-3 text-end">
            <a href="/forgot-password" style={{fontSize: "0.9rem"}}>Forgot Password?</a>
          </div>

          <button type="submit" className="btn btn-primary w-100 mb-3" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>

          {/* Google Sign-In Button */}
          {isFirebaseConfigured() && (
            <>
              <div className="text-center mb-2" style={{position: "relative"}}>
                <span style={{
                  backgroundColor: "white",
                  padding: "0 10px",
                  color: "#666",
                  fontSize: "0.9rem",
                  position: "relative",
                  zIndex: 1
                }}>or</span>
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  right: 0,
                  height: "1px",
                  backgroundColor: "#ddd",
                  zIndex: 0
                }}></div>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{ gap: "10px" }}
              >
                <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  <path fill="none" d="M0 0h48v48H0z"/>
                </svg>
                {loading ? "Signing in..." : "Sign in with Google"}
              </button>
            </>
          )}
        </form>

        <div className="text-center mt-4" style={{fontSize: "0.9rem"}}>
          Don't have an account? <a href="/signup">Sign up</a>
        </div>
      </div>
    </div>
  );
}

export default SignIn;
